import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useMotionTemplate, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, SparklesIcon, MapPinIcon, BedDoubleIcon, BathIcon, RulerIcon, GraduationCapIcon } from 'lucide-react';
import { MerchantBadge } from '../MerchantBadge';
import { AskAIButton } from '../AskAIButton';
import { merchantFor, LISTING_SELLERS } from '../../data/merchants';
import { ListingDetailModal } from '../ListingDetailModal';
import { useAuth } from '../AuthContext';
import type { ListingCardProps } from '../ListingCard';
const ICONS: Record<string, React.ElementType> = {
  Bedrooms: BedDoubleIcon,
  Bathrooms: BathIcon,
  Area: RulerIcon,
  School: GraduationCapIcon
};
const EASE = [0.23, 1, 0.32, 1] as const;
const SPRING = {
  stiffness: 220,
  damping: 24,
  mass: 0.6
};

/**
 * A home is chosen on place first. So the photograph runs tall and
 * uninterrupted, the community reads as the headline above the property type,
 * and bed / bath / area sit in one frosted band on the image the way a
 * property board reads.
 *
 * Hover treats the photo like a window into the home: the card tilts gently
 * toward the cursor, the photo drifts the other way as if seen through glass,
 * a soft pane of light follows the pointer, and the property board lifts.
 */
export function PropertyCard(props: ListingCardProps) {
  const {
    id,
    title,
    price,
    location,
    images,
    specs,
    matchScore,
    selected = false,
    onToggleSelect
  } = props;
  const {
    requireAuth
  } = useAuth();
  const reduce = useReducedMotion();
  const [frame, setFrame] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [details, setDetails] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Pointer position across the card, 0–1 on each axis (0.5 = centre).
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);
  const rotateY = useTransform(sx, [0, 1], [-5, 5]);
  const rotateX = useTransform(sy, [0, 1], [4, -4]);
  // The photo drifts against the tilt — the "looking through a window" depth.
  const photoX = useTransform(sx, [0, 1], [10, -10]);
  const photoY = useTransform(sy, [0, 1], [8, -8]);
  const lightX = useTransform(sx, v => `${v * 100}%`);
  const lightY = useTransform(sy, v => `${v * 100}%`);
  const light = useMotionTemplate`radial-gradient(260px circle at ${lightX} ${lightY}, rgba(255,255,255,0.28), transparent 65%)`;
  const onMove = (e: React.PointerEvent) => {
    if (reduce || e.pointerType !== 'mouse') return;
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    setHovered(false);
    px.set(0.5);
    py.set(0.5);
  };
  const merchant = merchantFor(LISTING_SELLERS[id] ?? location, 'Real estate agency');
  const step = (d: 1 | -1) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setDir(d);
    setFrame(f => (f + d + images.length) % images.length);
  };

  // "4BR Villa — Arabian Ranches" reads best as community over type.
  const [type, community] = title.includes('—') ? title.split('—').map(s => s.trim()) : [title, location.split(',')[0]];
  const band = specs.filter(s => s.label !== 'School').slice(0, 3);
  const school = specs.find(s => s.label === 'School');
  const [amount, period] = price.split('/');
  return <div className="h-full [perspective:1100px]" data-id="element-3171" data-mp="s47dmtb41xk0">
      <motion.div ref={cardRef} onPointerEnter={() => setHovered(true)} onPointerMove={onMove} onPointerLeave={onLeave} style={reduce ? undefined : {
      rotateX,
      rotateY
    }} animate={{
      y: hovered && !reduce ? -5 : 0
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className={`transform-gpu bg-white rounded-2xl border overflow-hidden shadow-soft hover:shadow-large transition-shadow duration-200 ease-out flex flex-col h-full group ${selected ? 'border-primary-indigo ring-2 ring-primary-indigo/25' : 'border-slate-200'}`} data-id="element-3172" data-mp="n6fm40edodgc">
        <div className="relative aspect-square overflow-hidden bg-slate-200" data-id="element-3173" data-mp="30fhz4jtgug27">
          {/* Photo — crossfades between frames and drifts with the pointer. */}
          <motion.div className="absolute -inset-3" style={reduce ? undefined : {
          x: photoX,
          y: photoY
        }} animate={{
          scale: hovered && !reduce ? 1.06 : 1
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-3174" data-mp="27prrmtmcacf2">
            <AnimatePresence initial={false} custom={dir} data-id="element-3175" data-mp="2lnzo70fgrbpp">
              <motion.img key={frame} src={images[frame]} alt={title} custom={dir} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              x: dir * 24,
              scale: 1.04
            }} animate={{
              opacity: 1,
              x: 0,
              scale: 1
            }} exit={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              x: dir * -24
            }} transition={{
              duration: 0.28,
              ease: EASE
            }} className="absolute inset-0 w-full h-full object-cover" draggable={false} data-id="element-3176" data-mp="2lpizecm9iwkm" />
            </AnimatePresence>
          </motion.div>

          {/* Light on glass — a soft pane that follows the pointer. */}
          {!reduce && <motion.div aria-hidden className="absolute inset-0 pointer-events-none mix-blend-soft-light" style={{
          backgroundImage: light
        }} animate={{
          opacity: hovered ? 1 : 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-3177" data-mp="3jh7n9vok20u5" />}

          <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2" data-id="element-3178" data-mp="1cq9ganc4f4h5">
            {matchScore > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-navy/75 backdrop-blur-sm text-[10.5px] font-bold text-white flex-shrink-0" data-id="element-3179" data-mp="18nl9el6yw7mt">
                <SparklesIcon className="w-3 h-3" data-id="element-3180" data-mp="36zwshav5hqqg" />
                {matchScore}%
              </span> : <span data-id="element-3181" data-mp="2t9kyf6f09hwo" />}
            <MerchantBadge merchant={merchant} variant="frosted" data-id="element-3182" data-mp="2x927e9tn4x6l" />
          </div>

          {/* The property board band — lifts off the photo on hover, and its
              facts settle in one after another. */}
          {band.length > 0 && <motion.div animate={{
          y: hovered && !reduce ? -4 : 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="absolute bottom-2 left-2 right-2 flex items-stretch rounded-lg bg-white/95 backdrop-blur-sm shadow-sm group-hover:shadow-medium transition-shadow duration-200 ease-out overflow-hidden" data-id="element-3183" data-mp="3pxh7lq845axv">
              {band.map((spec, i) => {
            const Icon = ICONS[spec.label] ?? RulerIcon;
            return <div key={spec.label} className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-1.5 py-1.5 ${i > 0 ? 'border-l border-slate-200/70' : ''}`} data-id="element-3184" data-mp="1wzu1y18iw13d" data-mpi={i}>
                    <motion.span animate={hovered && !reduce ? {
                scale: [1, 1.25, 1],
                rotate: [0, -8, 0]
              } : {
                scale: 1,
                rotate: 0
              }} transition={{
                duration: 0.28,
                ease: EASE,
                delay: hovered ? 0.04 + i * 0.05 : 0
              }} className="flex" data-id="element-3185" data-mp="26x8h7xipmju3">
                      <Icon className={`w-3 h-3 flex-shrink-0 transition-colors duration-200 ease-out ${hovered ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-3186" data-mp="vk61f4ofpyaz" />
                    </motion.span>
                    <span className="text-[11.5px] font-bold text-dark-navy truncate" data-id="element-3187" data-mp="52t9z96lclk8">
                      {spec.value}
                    </span>
                  </div>;
          })}
            </motion.div>}

          {images.length > 1 && <>
              <button onClick={step(-1)} aria-label="Previous photo" className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-slate-700 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 focus-visible:opacity-100 active:scale-95 transition-[opacity,transform] duration-200 ease-out" data-id="element-3188" data-mp="jzaiztrb5o3m">
                <ChevronLeftIcon className="w-4 h-4" data-id="element-3189" data-mp="12rl5id1nn9fk" />
              </button>
              <button onClick={step(1)} aria-label="Next photo" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-slate-700 opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 focus-visible:opacity-100 active:scale-95 transition-[opacity,transform] duration-200 ease-out" data-id="element-3190" data-mp="17q6bkt3l8nbx">
                <ChevronRightIcon className="w-4 h-4" data-id="element-3191" data-mp="2i8g8aqvx3h57" />
              </button>

              {/* Where you are in the tour — appears only while looking. */}
              <div className="absolute top-10 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out" data-id="element-3192" data-mp="3fvoyv0arzge2">
                {images.map((_, i) => <motion.span key={i} animate={{
              width: i === frame ? 14 : 5
            }} transition={{
              duration: 0.22,
              ease: EASE
            }} className={`h-[5px] rounded-full ${i === frame ? 'bg-white' : 'bg-white/55'}`} data-id="element-3193" data-mp="364kipx9d5phn" data-mpi={i} />)}
              </div>
            </>}
        </div>

        <div className="p-3 flex flex-col flex-1" data-id="element-3194" data-mp="2xsnbd7th3pf4">
          <div className="flex items-start justify-between gap-2" data-id="element-3195" data-mp="2sssn7c131dxr">
            <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1 min-w-0" data-id="element-3196" data-mp="2sulsamw1rq3n">
              {type}
            </h3>
            <span className="font-display font-bold text-[15px] text-dark-navy leading-snug flex-shrink-0" data-id="element-3197" data-mp="32qfc8rc3ilvg">
              {amount}
              {period && <span className="text-[11px] font-semibold text-slate-400" data-id="element-3198" data-mp="3godfp1ovoxw6">
                  /{period}
                </span>}
            </span>
          </div>
          <p className="inline-flex items-center gap-1 text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-3199" data-mp="3qg01tt2r8hdp">
            <motion.span animate={hovered && !reduce ? {
            y: [0, -3, 0]
          } : {
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE,
            delay: 0.08
          }} className="flex" data-id="element-3200" data-mp="3lhye2uudvtvw">
              <MapPinIcon className={`w-3 h-3 flex-shrink-0 transition-colors duration-200 ease-out ${hovered ? 'text-primary-indigo' : ''}`} data-id="element-3201" data-mp="hysbbwpfbp9h" />
            </motion.span>
            {community}
          </p>

          <div className="mt-auto pt-2.5" data-id="element-3202" data-mp="8x2jwqvjkqbp">
            {school && <p className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-3203" data-mp="dwfe5woqszob">
                <GraduationCapIcon className="w-3 h-3 flex-shrink-0" data-id="element-3204" data-mp="3vd6oy23pn4ar" />
                {school.value} to school
              </p>}
            <div className="flex items-center gap-1.5" data-id="element-3205" data-mp="402fekecbvmp">
              {onToggleSelect ? <AskAIButton selected={selected} onClick={onToggleSelect} className="flex-1" data-id="element-3206" data-mp="3meld0psquniy" /> : <span className="flex-1" data-id="element-3207" data-mp="3cfvviy4rf538" />}
              <button onClick={e => {
              e.stopPropagation();
              requireAuth(() => setDetails(true));
            }} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3208" data-mp="3hde1cfnhd470">
                Details
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <ListingDetailModal listing={props} open={details} onClose={() => setDetails(false)} data-id="element-3209" data-mp="25wb7ifmix4n9" />
    </div>;
}